import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

import { StyledDialog } from 'theme/index';

const REJECT_SUGGESTIONS = [
  'Blurry image',
  'Incorrect document',
  'Missing pages',
  'Duplicate upload',
];

interface RejectDialogProps {
  open: boolean;
  documentName?: string;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

const RejectDialog: React.FC<RejectDialogProps> = ({
  open,
  documentName,
  loading = false,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setReason('');
      setError('');
    }
  }, [open]);

  const handleConfirm = () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError('Reject reason is required.');
      return;
    }
    onConfirm(trimmed);
  };

  return (
    <StyledDialog open={open} onClose={loading ? undefined : onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Reject Document</DialogTitle>
      <DialogContent sx={{ flex: '0 0 auto' }}>
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: '100%',
            textAlign: 'center',
            py: 1,
          }}
        >
          <TextField
            label='Reject Reason'
            required
            fullWidth
            minRows={3}
            sx={{ mt: 1 }}
            value={reason}
            error={Boolean(error)}
            helperText={error ?? 'Provide a clear reason for the patient/guardian.'}
            disabled={loading}
            onChange={(event) => {
              setReason(event.target.value);
              if (error) {
                setError('');
              }
            }}
          />
          <Typography variant='caption' color='text.secondary' sx={{ mt: 1, display: 'block' }}>
            Examples: {REJECT_SUGGESTIONS.join(' · ')}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          justifyContent: 'center',
          gap: 1,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Button type='button' variant='outlined' onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          type='button'
          variant='contained'
          color='error'
          onClick={handleConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} color='inherit' /> : undefined}
        >
          Reject
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default RejectDialog;
